import { ReactElement, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import { isValidEmail } from '../../utils/EntityAttributesUtils'

interface MultiEmailFieldProps {
    /** Id for the inner input — the E2E suite fills this field by id. */
    inputId: string
    label: string
    value: string[]
    onChange: (emails: string[]) => void
    placeholder?: string
    disabled?: boolean
    /** Error from the parent form; the field's own parse errors take precedence while typing. */
    errorMessage?: string
    /** Address (lower-cased) -> server verdict. Anything listed in `blockingVerdicts` turns red. */
    verdicts?: Record<string, string>
    blockingVerdicts?: string[]
}

/**
 * UBQ-5212: enter several email addresses as chips.
 *
 * <p>Built on MUI's {@code Autocomplete} in {@code multiple freeSolo} mode so paste-splitting, chip
 * removal and keyboard behaviour come from MUI — and, more visibly, so the label notches into the
 * outline like every other field in the form. A hand-rolled bordered box cannot do that, which left
 * this control looking and sizing differently from its neighbours.
 *
 * <p>The field spans the dialog, so chips wrap inline and it grows as they are added. Deliberately no
 * maxHeight: MUI paints the outline as an absolutely-positioned fieldset, which cannot track a
 * scrolling sibling — the border would stop while the chips kept flowing past it.
 */
const MultiEmailField = ({
    inputId,
    label,
    value,
    onChange,
    placeholder,
    disabled = false,
    errorMessage,
    verdicts = {},
    blockingVerdicts = [],
}: MultiEmailFieldProps): ReactElement => {
    const { t } = useTranslation()
    const [error, setError] = useState('')
    // Controlled so `autoSelect` (which commits whatever is typed on blur) can't leave text behind.
    const [inputValue, setInputValue] = useState('')

    const isBlocked = (email: string): boolean =>
        blockingVerdicts.includes(verdicts[email.toLowerCase()] ?? '')

    /** Every entry is split, so one pasted "a@b.com, c@d.com" becomes two chips. */
    const commit = (candidates: string[]) => {
        const parts = candidates
            .flatMap((raw) => raw.split(/[,;\s]+/))
            .map((part) => part.trim())
            .filter(Boolean)
        const invalid = parts.filter((email) => !isValidEmail(email))
        // Names the offending addresses rather than just saying something is wrong — with a pasted
        // column of thirty, "which one?" is the only question that matters.
        setError(
            invalid.length
                ? `${t('invalidEmailmulti')}: ${invalid.join(', ')}`
                : '',
        )
        /* De-duplicated case-insensitively: the server keys its verdicts on the
           lower-cased address, so keeping both "A@x.com" and "a@x.com" would show one
           verdict against two chips and invite the same person twice. First spelling
           wins, so what was typed is what is displayed. */
        const seen = new Set<string>()
        const next = parts.filter(
            (email) =>
                isValidEmail(email) &&
                !seen.has(email.toLowerCase()) &&
                seen.add(email.toLowerCase()),
        )
        // Skip the update when nothing actually changed — otherwise a blur with an empty input hands the
        // parent a fresh array, which re-fires the debounced check-emails request for no reason.
        const unchanged =
            next.length === value.length &&
            next.every((email, index) => email === value[index])
        if (!unchanged) onChange(next)
        /* Clear the input on every commit — a value that de-duplicated to nothing new
           (UBQ-5510) leaves `value` unchanged, so MUI never fires its own 'reset' and the
           typed text would otherwise stay in the field. */
        setInputValue('')
    }

    const message = error || errorMessage || ''

    return (
        <>
            <Autocomplete<string, true, false, true>
                multiple
                freeSolo
                /* Set on the Autocomplete, NOT just the TextField below. Autocomplete
                   generates its own id and passes it down through `params.inputProps`,
                   which MUI applies straight onto the <input> — so a TextField-level
                   `id` is overridden there and the input ends up as ":r0:". Every kiwi
                   locator for this field is //input[@id='add-user-email'], so that
                   silently broke Add User for the whole E2E suite. Setting it here makes
                   params.id, the input id, the label's htmlFor and the listbox/option
                   ids all derive from one value. */
                id={inputId}
                // Commits a half-typed address when focus leaves, rather than silently dropping it.
                autoSelect
                /* UBQ-5510: keep `commit` the single authority on de-duplication. In `multiple`
                   mode MUI compares each committed value against the current chips and silently
                   swallows one it considers already present — without clearing the typed text,
                   so a duplicate lingered in the input until a later commit swept it away (taking
                   the fresh copy with it and leaving only the first). Forcing every value to look
                   new lets our own onChange -> commit run on every Enter/blur; commit then
                   de-dupes and clears the input, so a duplicate is discarded on the first blur. */
                isOptionEqualToValue={() => false}
                /* UBQ-5543: MUI vertically centres the clear ("X") end-adornment
                   (default top: calc(50% - 14px)), so once chips wrap and the field
                   grows tall the centre drops to the bottom-right corner. A fixed
                   `top` must serve both heights: 26px centres the 28px clear button
                   on the single chip-row, so a single-line field looks right while a
                   tall multi-line field keeps the X pinned to that first row instead
                   of sliding down. */
                sx={{
                    '& .MuiAutocomplete-endAdornment': {
                        top: 26,
                    },
                    /* UBQ-5543: the chips and the typed text default to ~13px, which
                       reads small against the rest of the form — pin both to the app's
                       14px body size. */
                    '& .MuiInputBase-input': { fontSize: '14px' },
                    '& .MuiChip-label': { fontSize: '14px' },
                    /* UBQ-5544: the chips and the <input> share one flex-wrap row, and
                       MUI lets the input shrink to a 30px stub — so once the chips fill
                       a row the input clings to the leftover space with the caret and
                       the "up to N" placeholder crammed into a sliver, and the next
                       address is typed into that sliver instead of a fresh line. Demand
                       the full row width for the input so it can never sit beside a
                       chip: flex-wrap then always drops it onto its own line below the
                       chips, giving the placeholder and the address being typed the full
                       width of the field. (Also correct when there are no chips — the
                       input is the only item and simply fills its line.) */
                    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                        minWidth: '100%',
                    },
                }}
                options={[]}
                value={value}
                inputValue={inputValue}
                onInputChange={(_event, next, reason) =>
                    // 'reset' fires once a chip is committed; keeping the text would leave the address in
                    // the input as well as on a chip.
                    setInputValue(reason === 'reset' ? '' : next)
                }
                onChange={(_event, next) => commit(next as string[])}
                disabled={disabled}
                renderTags={(emails, getTagProps) =>
                    emails.map((email, index) => {
                        // MUI puts `key` inside getTagProps; spreading it into Chip is a React warning.
                        const { key, ...tagProps } = getTagProps({ index })
                        return (
                            <Chip
                                {...tagProps}
                                key={key ?? email}
                                /* Kebab-case and index-parameterized, per the E2E id
                                   convention. An email as an id puts '@' and '.' into a
                                   selector, which XPath and CSS both have to escape. */
                                id={`${inputId}-chip-${index}`}
                                size="small"
                                label={email}
                                color={isBlocked(email) ? 'error' : 'default'}
                                sx={{ margin: '2px' }}
                            />
                        )
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        id={inputId}
                        name="email"
                        label={label}
                        placeholder={placeholder}
                        error={!!message}
                        // Omitted rather than empty: an always-rendered helper reserves a line of
                        // space under the field, which the count row then has to fight.
                        helperText={message || undefined}
                        // A column pasted out of Excel arrives newline-separated, and a single-line
                        // <input> strips newlines — concatenating the addresses into one unusable
                        // string. Autocomplete also only commits on Enter/blur, so the paste is
                        // intercepted and committed here rather than left sitting in the input.
                        onPaste={(event) => {
                            const text = event.clipboardData.getData('text')
                            if (!/[,;\s]/.test(text)) return
                            event.preventDefault()
                            // inputValue is carried in rather than dropped: pasting a list after
                            // half-typing an address used to silently discard what was typed.
                            commit([...value, inputValue, text])
                            setInputValue('')
                        }}
                    />
                )}
            />
            {value.length > 0 && (
                // Once the list is long the field alone no longer says how many people are about to be
                // emailed, and removing them one chip at a time is tedious.
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        // 14px is MUI's helper-text gutter, so the count lines up with the field's text
                        // and Clear All stops short of the outline instead of being clipped by it.
                        margin: '4px 14px 0',
                    }}
                >
                    {/* UBQ-5543: 12px standard helper size — caption alone renders
                        10.5px against the 14px theme base. */}
                    <Typography
                        variant="caption"
                        sx={{ opacity: 0.7, fontSize: 12 }}
                    >
                        {t('emailsAdded', { count: value.length })}
                    </Typography>
                    <Link
                        component="button"
                        type="button"
                        variant="caption"
                        sx={{ fontSize: 12 }}
                        id={`${inputId}-clear-all`}
                        disabled={disabled}
                        onClick={() => {
                            setError('')
                            onChange([])
                        }}
                    >
                        {t('clearAll')}
                    </Link>
                </Box>
            )}
        </>
    )
}

export default MultiEmailField
